/**
 * スキル配列と既存スキル配列を skill_id または skill_name で突き合わせて
 * 更新／削除／追加のattributesを生成
 * @param {Array} skills - 現在のスキル配列
 * @param {Array} existingSkills - DBの既存スキル配列
 * @param {boolean} isEditMode - 編集モードかどうか
 * @returns {Object} skills_attributes
 */
export const transformSkillsAttributes = (
  skills,
  existingSkills,
  isEditMode
) => {
  const attributes = {};
  let attributeIndex = 0;

  if (isEditMode && existingSkills) {
    // 既存（DB）側を基準に「更新」か「削除」判定
    existingSkills.forEach((existSkill) => {
      // skill_id または skill_name が一致するものを探す
      const currentSkill = skills?.find(
        (s) =>
          (s.skill_id &&
            existSkill.skill_id &&
            String(s.skill_id) === String(existSkill.skill_id)) ||
          (s.skill_name &&
            existSkill.skill_name &&
            s.skill_name.trim() === existSkill.skill_name.trim())
      );
      attributes[attributeIndex++] = {
        id: existSkill.id,
        skill_id: currentSkill?.skill_id || existSkill.skill_id || "",
        skill_name: currentSkill?.skill_name || existSkill.skill_name || "",
        experience_year:
          currentSkill?.experience_year || existSkill.experience_year || "",
        level: currentSkill?.level || existSkill.level || "",
        visible_flag:
          currentSkill?.visible_flag !== undefined
            ? !!currentSkill.visible_flag
            : !!existSkill.visible_flag,
        _destroy: currentSkill ? "false" : "true",
      };
    });

    // 現在の配列で「既存にない」ものは新規追加
    if (skills && skills.length > 0) {
      skills.forEach((skill) => {
        if (!skill.skill_name && !skill.skill_id) return; // 空行は除外
        // 既存（DB）に同じskill_id or skill_nameがなければ追加
        const isNewSkill = !existingSkills.some(
          (exist) =>
            (skill.skill_id &&
              exist.skill_id &&
              String(skill.skill_id) === String(exist.skill_id)) ||
            (skill.skill_name &&
              exist.skill_name &&
              skill.skill_name.trim() === exist.skill_name.trim())
        );
        if (isNewSkill) {
          attributes[attributeIndex++] = {
            skill_id: skill.skill_id || "",
            skill_name: skill.skill_name || "",
            experience_year: skill.experience_year || "",
            level: skill.level || "",
            visible_flag:
              skill.visible_flag !== undefined ? !!skill.visible_flag : true,
            _destroy: "false",
          };
        }
      });
    }
  } else {
    // 新規作成モード
    if (skills && skills.length > 0) {
      skills.forEach((skill, idx) => {
        if (!skill.skill_name && !skill.skill_id) return; // 空行は除外
        attributes[idx.toString()] = {
          skill_id: skill.skill_id || "",
          skill_name: skill.skill_name || "",
          experience_year: skill.experience_year || "",
          level: skill.level || "",
          visible_flag:
            skill.visible_flag !== undefined ? !!skill.visible_flag : true,
          _destroy: "false",
        };
      });
    }
  }

  return attributes;
};

/**
 * 工程データをRails向けのattributes形式に変換する
 * @param {Array} currentOperations - 現在のフォーム上の工程
 * @param {Array} existingOperations - DBから読み込んだ既存工程
 * @param {boolean} isEditMode - 編集モードかどうか
 * @returns {Object} project_operations_attributes形式
 */
export const transformOperationsAttributes = (
  currentOperations,
  existingOperations = [],
  isEditMode
) => {
  const attributes = {};
  let attributeIndex = 0;

  if (isEditMode && existingOperations.length > 0) {
    // 既存（DB）側を基準に「更新」か「削除」判定（operation_id一致で判定）
    existingOperations.forEach((existingOp) => {
      const matched = currentOperations.find(
        (op) =>
          op.operation_id &&
          existingOp.operation_id &&
          String(op.operation_id) === String(existingOp.operation_id)
      );
      attributes[attributeIndex++] = {
        id: existingOp.id,
        operation_id: matched?.operation_id || existingOp.operation_id || "",
        experience_year:
          matched?.experience_year || existingOp.experience_year || "",
        _destroy: matched ? "false" : "true",
      };
    });

    // 新規（既存にないoperation_idのみ）
    currentOperations.forEach((op) => {
      if (!op.operation_id) return;
      const isNew = !existingOperations.some(
        (exist) =>
          op.operation_id &&
          exist.operation_id &&
          String(op.operation_id) === String(exist.operation_id)
      );
      if (isNew) {
        attributes[attributeIndex++] = {
          operation_id: op.operation_id,
          experience_year: op.experience_year || "",
          _destroy: "false",
        };
      }
    });
  } else {
    // 新規作成モード
    currentOperations.forEach((op) => {
      if (op.operation_id) {
        attributes[attributeIndex++] = {
          operation_id: op.operation_id,
          experience_year: op.experience_year || "",
          _destroy: "false",
        };
      }
    });
  }
  return attributes;
};

/**
 * 役割データをRails向けのattributes形式に変換する
 * @param {Array} currentRoles - 現在のフォーム上の役割
 * @param {Array} existingRoles - DBから読み込んだ既存役割
 * @param {boolean} isEditMode - 編集モードかどうか
 * @returns {Object} project_roles_attributes形式
 */
export const transformRolesAttributes = (
  currentRoles,
  existingRoles = [],
  isEditMode
) => {
  const attributes = {};
  let attributeIndex = 0;

  if (isEditMode && existingRoles.length > 0) {
    // 既存（DB）側を基準に「更新」か「削除」判定（role_id一致で判定）
    existingRoles.forEach((existingRole) => {
      const matched = currentRoles.find(
        (r) =>
          r.role_id &&
          existingRole.role_id &&
          String(r.role_id) === String(existingRole.role_id)
      );
      attributes[attributeIndex++] = {
        id: existingRole.id,
        role_id: matched?.role_id || existingRole.role_id || "",
        experience_year:
          matched?.experience_year || existingRole.experience_year || "",
        _destroy: matched ? "false" : "true",
      };
    });

    // 新規（既存にないrole_idのみ）
    currentRoles.forEach((role) => {
      if (!role.role_id) return;
      const isNew = !existingRoles.some(
        (exist) =>
          role.role_id &&
          exist.role_id &&
          String(role.role_id) === String(exist.role_id)
      );
      if (isNew) {
        attributes[attributeIndex++] = {
          role_id: role.role_id,
          experience_year: role.experience_year || "",
          _destroy: "false",
        };
      }
    });
  } else {
    // 新規作成モード
    currentRoles.forEach((role) => {
      if (role.role_id) {
        attributes[attributeIndex++] = {
          role_id: role.role_id,
          experience_year: role.experience_year || "",
          _destroy: "false",
        };
      }
    });
  }
  return attributes;
};
